import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Modal from '../../components/modal/modal.component';
import {disableSwipeToClose} from "../../utils/tools/modals";
import {AndroidSettings, IOSSettings, NativeSettings} from "capacitor-native-settings";
import {IonAlert, IonCol, IonLoading, IonRow, IonToast} from "@ionic/react";
import {StyledModalContent} from "@components/modal/modal.style";
import {ToastModel} from "types/toast";
import CloseImage from "@assets/images/e-remove.svg";
import DateInput from "@components/form/input/date.component";
import moment from "moment";
import {StyledButton} from "@components/button/button.style";
import {Geolocation} from "@capacitor/geolocation";
import {leave} from "@services/geolocation.service";
import TimeInput from "@components/form/input/time.component";
import {getServerNow} from "@services/serverDate.service";

type LocationLeaveModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

const LocationLeaveModal: React.FC<LocationLeaveModalProps> = (props: LocationLeaveModalProps) => {

	const {t} = useTranslation();
	const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);
	const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
	const [showLoading, setShowLoading] = useState(false);
	const [toast, setToast] = useState<ToastModel>();
	const [endDate, setEndDate] = useState<string>();
	const [endTime, setEndTime] = useState<string>();

	const handleClose = () => {
		props.onClose();
	};

	const handleOpen = () => {
		setShowLocationModal(props.isOpen);
	};

	useEffect(() => {
		handleOpen();
	}, [props.isOpen]);

	const handleSubmit = async () => {
		let hasPermissions = await Geolocation.checkPermissions()
			.then((location) => {
				return location.location;
			})
			.catch(reason => {
				return 'error';
			});
		setShowLoading(true)
		if (hasPermissions === 'denied' || hasPermissions === 'error') {
			setShowPermissionAlert(true);
		} else {
			if (endDate && endTime) {
				let endAt = moment(endDate).format('YYYY-MM-DD') + ' ' + moment(endTime).format('HH:mm');
				await leave(endAt)
					.then((response) => {
						handleClose();
					})
					.catch(error => {
						if (error.response.status === 409) {
							setToast({
								message: t('locationLeaveModal.leaveDateCantBeInTheFuture'),
								type: 'danger'
							});
						} else if (error.response.status === 406) {
							setToast({
								message: t('locationLeaveModal.leaveDateCantBeBeforeStart'),
								type: 'danger'
							});
						} else {
							setToast({
								message: t('locationLeaveModal.error'),
								type: 'danger'
							});
						}
					})
			}

		}
		setShowLoading(false)
	}

	const getMaxTime = () => {
		return endDate ? (moment(endDate).format('YYYY-MM-DD') == getServerNow().format('YYYY-MM-DD') ? getServerNow().format() : undefined) : getServerNow().format();
	}

	return (
		<>
			<Modal
				isOpen={showLocationModal}
				canDismiss={disableSwipeToClose}
				maxHeight={'50%'}
				disableClose={true}
				onDidDismiss={handleClose}>

				<StyledModalContent>
					<img src={CloseImage} className="close" onClick={() => handleClose()}/>
					<h1 className="title">{t("locationLeaveModal.title")}</h1>
					<div className="content full-height">
						<IonRow>
							<IonCol size="12" className="label">
								<small>{t('locationLeaveModal.dateTime')}</small>
							</IonCol>
						</IonRow>
						<IonRow>
							<IonCol size="8" className="date-field">
								<DateInput
									presentation="date"
									format="DD.MM.YYYY"
									cancelText={t("common.dateCancel")}
									doneText={t("common.dateDone")}
									placeholder={t("common.select")}
									onChange={value => {
										setEndDate(value)
										setEndTime(undefined)
									}}
									max={getServerNow().format('YYYY-MM-DD')}
									value={endDate}
								/>
							</IonCol>
							<IonCol size="4" className="date-field">
								<TimeInput
									keepContentsMounted={true}
									cancelText={t("common.dateCancel")}
									doneText={t("common.dateDone")}
									placeholder={t("common.select")}
									onChange={value => setEndTime(value)}
									disabled={!endDate}
									// max={getMaxTime()}
									value={endTime}
								/>
							</IonCol>
						</IonRow>

						<StyledButton disabled={!endDate || !endTime} onClick={handleSubmit}>{t('locationLeaveModal.save')}</StyledButton>
					</div>
				</StyledModalContent>
			</Modal>

			<IonAlert
				isOpen={showPermissionAlert}
				onDidDismiss={() => setShowPermissionAlert(false)}
				header={t('common.geolocation_permissions.alert')}
				buttons={[
					{
						text: t('common.alertCancel'),
						role: 'cancel',
						cssClass: 'secondary',
						handler: () => {
							setShowPermissionAlert(false);
						},
					},
					{
						text: t('common.geolocation_permissions.goToAppSettings'),
						handler: async () => {
							setShowPermissionAlert(false);
							await NativeSettings.open({
								optionAndroid: AndroidSettings.ApplicationDetails,
								optionIOS: IOSSettings.App
							})
						}
					}
				]}
			/>

			<IonLoading onDidDismiss={() => setShowLoading(false)} isOpen={showLoading}/>

			<IonToast
				isOpen={!!toast}
				onDidDismiss={() => setToast(undefined)}
				message={toast?.message}
				duration={toast?.duration ? toast.duration : 6000}
				position="top"
				color={toast?.type}
			/>
		</>
	);
};

export default LocationLeaveModal;
